import { useCallback, useEffect, useMemo } from 'react';
import { useChatsContext } from '@/contexts/chats';
import type { ChatListItem } from '@/services/chats';

export type UseChatsResult = {
  chats: ChatListItem[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  create: (title: string) => Promise<ChatListItem | null>;
  remove: (chatId: string | number) => Promise<boolean>;
};

type UseChatsOptions = {
  skipLoad?: boolean;
  sheetId?: number;
};

export const useChats = (
  accountId?: string | number | null,
  options: UseChatsOptions = {}
): UseChatsResult => {
  const { skipLoad = false, sheetId } = options;
  const { state, load, create: createChat, remove: removeChat } = useChatsContext();
  const key = accountId != null ? (sheetId != null ? `${accountId}:sheet:${sheetId}` : String(accountId)) : null;

  const bucket = key ? state[key] : undefined;

  useEffect(() => {
    if (accountId == null) return;
    if (skipLoad) return;
    void load(accountId, { sheetId });
  }, [accountId, load, skipLoad, sheetId]);

  const loading = useMemo(() => {
    if (skipLoad) return false;
    if (accountId == null) return false;
    if (!bucket) return true;
    return bucket.status === 'loading' || bucket.status === 'idle';
  }, [accountId, bucket, skipLoad]);

  const error = bucket?.error ?? null;
  const chats = bucket?.data ?? [];

  const refetch = useCallback(async () => {
    if (accountId == null) return;
    await load(accountId, { force: true, sheetId });
  }, [accountId, load, sheetId]);

  const create = useCallback<UseChatsResult['create']>(
    async title => {
      if (accountId == null) return null;
      return createChat(accountId, title);
    },
    [accountId, createChat]
  );

  const remove = useCallback<UseChatsResult['remove']>(
    async chatIdToDelete => {
      if (accountId == null) return false;
      return removeChat(accountId, chatIdToDelete);
    },
    [accountId, removeChat]
  );

  return { chats, loading, error, refetch, create, remove };
};

export default useChats;
